// The cup mark (clan-cup.md §7, card 09 of mockup round 078, R1634).
//
// A small gold cup beside the tag of the clan that won the last clan cup,
// wherever the chrome names a clan by its tag: the top bar of a world, the
// clan list and the clan heading of the CLAN section, the clan ranking of
// the season card and the rows of STANDINGS. It stands at the height of
// the tag, so it never outranks the name. A hover names the cup through
// the title; a press names it beside the mark for a few seconds, because a
// finger has no hover. The map draws no mark: a hull wears no clan tag.
//
// The holder comes from `GET /api/cup/mark`, read ONCE per page: there is
// at most one holder at a time, and it changes once a month. A page with
// no meta server behind it reads no holder and draws no mark.

import { getCupMark, type CupMark } from '../net/api';
import { hubCup as S } from '../gen/strings';
import { esc } from './esc';

/** The holder, null for none, and whether the one read has answered. */
let holder: CupMark | null = null;
let state: 'idle' | 'loading' | 'ready' = 'idle';
/** The number of the current read. A reset starts a new number, so the
 *  answer of an older read changes nothing. */
let read = 0;
const listeners: Array<() => void> = [];
let tipsWired = false;

/**
 * The clan that holds the cup mark now, or null. The first call starts the
 * one read of the page; the answer calls every `onCupHolder` listener.
 */
export function cupHolder(): CupMark | null {
  if (state === 'idle') {
    state = 'loading';
    const mine = ++read;
    getCupMark().then((m) => { if (mine === read) holder = m ?? null; }, () => { if (mine === read) holder = null; }).finally(() => {
      if (mine !== read) return;
      state = 'ready';
      for (const fn of listeners) fn();
    });
  }
  return holder;
}

/** Calls `fn` once the holder has answered: a screen draws again then. */
export function onCupHolder(fn: () => void): void {
  listeners.push(fn);
}

/** Sets the holder. Tests only. */
export function setCupHolderForTest(m: CupMark | null): void {
  holder = m;
  state = 'ready';
}

/** Puts the holder back to "not read yet", so the next `cupHolder` call
 *  starts a new read. The listeners stay: a module subscribes once, when
 *  it loads. Tests only (`vitest.node.setup.ts`). */
export function resetCupHolderForTest(): void {
  holder = null;
  state = 'idle';
  read++;
}

/** The name of the month of a cup: "OCTOBER" for "2026-10". A month the
 *  table does not hold reads as the key itself. */
export function monthName(month: string): string {
  return S.months[(Number.parseInt(month.split('-')[1], 10) + 11) % 12] ?? month;
}

/** The name of one cup: "OCTOBER CUP 2026". `month` is "2026-10". */
export function cupName(month: string): string {
  const y = Number.parseInt(month.split('-')[0], 10);
  return S.cup_name_year({ month: monthName(month), year: String(y) });
}

/** The gold cup of the mark, `size` px square. It takes the colour of its
 *  box (`.cupm`), so a stylesheet gives it the gold token. */
export function cupSvg(size: number): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">`
    + '<path d="M3.6 1.6 H12.4 V3.6 C12.4 7.2 10.4 9.4 8 9.4 C5.6 9.4 3.6 7.2 3.6 3.6 Z"/>'
    + '<path d="M3.7 2.8 C1.2 2.8 1.2 6.8 4.4 6.8 M12.3 2.8 C14.8 2.8 14.8 6.8 11.6 6.8" fill="none" '
    + 'stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>'
    + '<rect x="7.2" y="9.2" width="1.6" height="2.8"/><rect x="4.8" y="12" width="6.4" height="1.9" rx=".5"/></svg>';
}

/** The mark of one cup at `size` px, with the name a hover and a press
 *  show. */
export function cupMarkOf(month: string, size: number): string {
  wireTips();
  const name = cupName(month);
  return `<span class="cupm" role="img" tabindex="0" title="${esc(name)}" `
    + `aria-label="${esc(S.mark_label({ cup: name }))}" data-cup="${esc(name)}">${cupSvg(size)}</span>`;
}

/**
 * The mark beside one clan tag, or '' when that clan holds no cup mark
 * now. `nowSec` is the instant in unix seconds; the mark ends at the
 * holder's `until` on the reader's clock too.
 */
export function cupMarkFor(tag: string | undefined, size = 12, nowSec = Date.now() / 1000): string {
  const h = cupHolder();
  if (h === null || !tag || tag.toUpperCase() !== h.tag.toUpperCase() || nowSec >= h.until) return '';
  return cupMarkOf(h.month, size);
}

/** A press on a mark names the cup beside it, and a second press takes the
 *  name away; the next draw of the screen takes it away as well. One
 *  listener on the document serves every mark of every screen. */
function wireTips(): void {
  if (tipsWired || typeof document === 'undefined') return;
  tipsWired = true;
  document.addEventListener('pointerdown', (ev) => {
    const t = ev.target;
    if (!(t instanceof Element)) return;
    t.closest<HTMLElement>('.cupm')?.classList.toggle('tip');
  });
}
